import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { FeatureFlags, UserSubscription } from '@/types/subscription';
import type { User } from '@common/schemas/user.schema';

import { useFirebase } from '@/firebase';
import { isTerminalSubscription } from '@/lib/subscription-status';

export interface UserSessionContextState {
  user: User | null;
  loading: boolean;
  error: Error | null;
  hasFeature: (featureKey: string) => boolean;
  isAdmin: boolean;
  isTokenDepleted: boolean;
  tokensSpent: number;
  maxTokens: number;
  tokensRemaining: number;
}

export const UserSessionContext = createContext<UserSessionContextState | undefined>(undefined);

type UserWithTokenQuota = User & {
  maxTokens?: number;
  tokenUsageId?: string | null;
};

export function UserSessionProvider({ children }: { children: React.ReactNode }) {
  const { user: authUser, backend, isUserLoading: isAuthLoading } = useFirebase();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [dbUser, setDbUser] = useState<UserWithTokenQuota | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [hasAttemptedCreate, setHasAttemptedCreate] = useState(false);

  // Reset attempt status if user changes
  useEffect(() => {
    setHasAttemptedCreate(false);
    setDbUser(null);
    setCurrentSubscription(null);
  }, [authUser?.uid]);

  useEffect(() => {
    if (isAuthLoading) {
      setLoading(true);
      return;
    }

    if (!authUser || !backend) {
      setFeatureFlags({});
      setIsAdmin(false);
      setDbUser(null);
      setCurrentSubscription(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    let disposed = false;

    // Listen to the root user profile for identity, feature flags, quota, and usage.
    const unsubscribeUser = backend.users.subscribeUserProfile(
      authUser.uid,
      (userData) => {
        if (disposed) return;

        if (!userData) {
          setDbUser(null);
          
          // Trigger user creation if document is missing and we haven't tried yet
          if (!hasAttemptedCreate && !isInitializing) {
            setIsInitializing(true);
            setHasAttemptedCreate(true);

            backend.users.initializeUserProfile()
              .catch((err) => {
                console.error('Failed to initialize user profile:', err);
              })
              .finally(() => {
                setIsInitializing(false);
              });
          } else if (!isInitializing) {
            setLoading(false);
          }
          return;
        }

        setIsAdmin(userData.isAdmin);
        setFeatureFlags(userData.flags || {});
        setDbUser(userData as UserWithTokenQuota);
        setLoading(false);
      },
      (err) => {
        if (disposed) return;
        console.error('Error fetching user profile:', err);
        setError(err);
        setLoading(false);
      }
    );

    // Keep billing status and dates live from users/{uid}/subscription/current.
    const unsubscribeSubscription = backend.billing.subscribeSubscription(
      authUser.uid,
      (subscription) => {
        if (disposed) return;
        setCurrentSubscription(subscription);
        setLoading(false);
      },
      (err) => {
        if (disposed) return;
        console.error('Error fetching subscription:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      disposed = true;
      unsubscribeUser();
      unsubscribeSubscription();
    };
  }, [authUser, backend, isAuthLoading, hasAttemptedCreate, isInitializing]);

  const effectiveCurrentSubscription = useMemo(() => {
    return currentSubscription ?? dbUser?.subscription?.current ?? null;
  }, [currentSubscription, dbUser]);

  const hasTerminalSubscription = useMemo(() => {
    return isTerminalSubscription(effectiveCurrentSubscription);
  }, [effectiveCurrentSubscription]);

  const hasFeature = (featureKey: string): boolean => {
    if (hasTerminalSubscription) return false;
    return featureFlags[featureKey] ?? false;
  };

  const maxTokens = useMemo(() => {
    if (hasTerminalSubscription) return 0;

    return dbUser?.maxTokens ??
      currentSubscription?.maxTokens ??
      dbUser?.subscription?.current?.maxTokens ??
      0;
  }, [currentSubscription, dbUser, hasTerminalSubscription]);

  const tokenUsageId = useMemo(() => {
    if (hasTerminalSubscription) return undefined;

    if (dbUser?.tokenUsageId !== undefined) {
      return dbUser.tokenUsageId ?? undefined;
    }

    return currentSubscription?.tokenUsageId ??
      dbUser?.subscription?.current?.tokenUsageId;
  }, [currentSubscription, dbUser, hasTerminalSubscription]);

  const mergedUser = useMemo<User | null>(() => {
    if (!dbUser) return null;

    const embeddedCurrent = dbUser.subscription?.current;
    const billingCurrent = currentSubscription ?? embeddedCurrent;

    return {
      ...dbUser,
      ...(hasTerminalSubscription ? {
        planId: 'free',
        planName: 'Free',
        hasActiveSubscription: false,
      } : {}),
      maxTokens,
      tokenUsageId: tokenUsageId ?? null,
      subscription: billingCurrent ? {
        ...dbUser.subscription,
        current: {
          ...embeddedCurrent,
          ...billingCurrent,
          maxTokens,
          tokenUsageId,
        },
      } : dbUser.subscription,
    } as UserWithTokenQuota;
  }, [currentSubscription, dbUser, hasTerminalSubscription, maxTokens, tokenUsageId]);

  const tokensSpent = useMemo(() => {
    const usageId = tokenUsageId || new Date().toISOString().slice(0, 7);
    return dbUser?.tokenUsage?.[usageId]?.totalTokensSpent || 0;
  }, [dbUser, tokenUsageId]);

  const tokensRemaining = useMemo(() => {
    return Math.max(maxTokens - tokensSpent, 0);
  }, [maxTokens, tokensSpent]);

  const isTokenDepleted = useMemo(() => {
    return tokensSpent >= maxTokens && maxTokens > 0;
  }, [tokensSpent, maxTokens]);

  const contextValue = useMemo(() => ({
    user: mergedUser,
    loading,
    error,
    hasFeature,
    isAdmin,
    isTokenDepleted,
    tokensSpent,
    maxTokens,
    tokensRemaining,
  }), [mergedUser, loading, error, isAdmin, isTokenDepleted, tokensSpent, maxTokens, tokensRemaining, featureFlags]);

  return (
    <UserSessionContext.Provider value={contextValue}>
      {children}
    </UserSessionContext.Provider>
  );
}

export function useUserSession() {
  const context = useContext(UserSessionContext);
  if (context === undefined) {
    throw new Error('useUserSession must be used within a UserSessionProvider');
  }
  return context;
}
